import { useId, type ReactNode } from 'react';
import { AVATARS_BY_ID, type AvatarId } from '@shared/game/types';

interface AvatarProps {
  avatar: AvatarId;
  size?: number;
  className?: string;
  decorative?: boolean;
}

export function Avatar({ avatar, size = 56, className = '', decorative = false }: AvatarProps) {
  const definition = AVATARS_BY_ID[avatar] ?? AVATARS_BY_ID.aurora;
  const { palette, kind, name, serial } = definition;
  const rawId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const bg = `holo-bg-${rawId}`;
  const shade = `holo-shade-${rawId}`;
  const scan = `holo-scan-${rawId}`;

  const eyes = (
    <g>
      <rect x="34" y="52" width="11" height="5" rx="2.5" fill={palette.glow} />
      <rect x="55" y="52" width="11" height="5" rx="2.5" fill={palette.glow} />
      <rect x="36" y="53.5" width="7" height="1.5" rx=".75" fill="#ffffff" opacity=".85" />
      <rect x="57" y="53.5" width="7" height="1.5" rx=".75" fill="#ffffff" opacity=".85" />
    </g>
  );

  const shoulders = (
    <g>
      <path d="M12 100c5-19 18-29 38-29s33 10 38 29H12Z" fill={palette.deep} />
      <path d="M20 100c4-10 10-16 18-20l5 20H20ZM80 100c-4-10-10-16-18-20l-5 20h23Z" fill={palette.skin} opacity=".45" />
      <path d="M28 92h8M64 92h8" stroke={palette.accent} strokeWidth="1.2" strokeLinecap="round" opacity=".7" />
      <circle cx="24" cy="96" r="2" fill={palette.accent} />
      <circle cx="76" cy="96" r="2" fill={palette.accent} />
    </g>
  );

  let portrait: ReactNode;

  switch (kind) {
    case 'mech':
      portrait = (
        <g>
          {shoulders}
          <path d="M50 18v9" stroke={palette.skin} strokeWidth="4" strokeLinecap="round" />
          <circle cx="50" cy="16" r="4.5" fill={palette.accent} />
          <path d="M25 40 32 25l18-6 18 6 7 15-3 35-22 10-22-10-3-35Z" fill={palette.skin} />
          <path d="m29 43 7-12 14-4 14 4 7 12-4 9H33l-4-9Z" fill={palette.deep} />
          <path d="M31 41h38l-2 6H33l-2-6Z" fill={palette.accent} opacity=".2" />
          {eyes}
          <path d="M40 70h20l-3 7H43l-3-7Z" fill={palette.deep} />
          <path d="M44 71v5M50 71v5M56 71v5" stroke={palette.skin} strokeWidth="1" opacity=".4" />
          <path d="M18 51h7v15h-7zM75 51h7v15h-7z" fill={palette.accent} opacity=".55" />
          <path d="M33 29h8l2-6 7 6 7-6 2 6h8" fill="none" stroke={palette.accent} strokeWidth="1.3" opacity=".8" />
          <path d="M36 80h28" stroke={palette.accent} strokeWidth="1" opacity=".35" />
        </g>
      );
      break;
    case 'synth':
      portrait = (
        <g>
          {shoulders}
          <path d="M50 18c-14 0-25 11-27 24l8 27 19 9 19-9 8-27c-2-13-13-24-27-24Z" fill={palette.skin} opacity=".9" />
          <path d="M25 45c-7-7-9-17-4-24 5 4 10 6 16 7M75 45c7-7 9-17 4-24-5 4-10 6-16 7" fill={palette.deep} />
          <path d="M29 41c3-11 10-17 21-17s18 6 21 17l-3 20-18 9-18-9-3-20Z" fill={palette.deep} opacity=".7" />
          <path d="M32 46h36l-2 12-16 7-16-7-2-12Z" fill="#050a13" opacity=".6" />
          {eyes}
          <path d="M41 69c5 2 13 2 18 0" stroke={palette.accent} strokeWidth="1.2" strokeLinecap="round" opacity=".5" />
          <path d="M24 52h-6M82 52h-6" stroke={palette.accent} strokeWidth="1.5" opacity=".6" />
          <circle cx="28" cy="32" r="2" fill={palette.glow} />
          <circle cx="72" cy="32" r="2" fill={palette.glow} />
          <path d="M37 24h26" stroke={palette.accent} strokeWidth="1" opacity=".35" />
        </g>
      );
      break;
    case 'drone':
      portrait = (
        <g>
          <path d="M22 72c0-12 12-22 28-22s28 10 28 22v8c0 10-12 17-28 17s-28-7-28-17v-8Z" fill={palette.deep} />
          <circle cx="50" cy="57" r="27" fill={palette.skin} opacity=".9" />
          <path d="M28 50c4-9 11-14 22-14s18 5 22 14" stroke={palette.accent} strokeWidth="2" fill="none" opacity=".7" />
          <rect x="31" y="48" width="38" height="18" rx="9" fill="#050a13" />
          <circle cx="41" cy="57" r="5" fill={palette.glow} />
          <circle cx="59" cy="57" r="5" fill={palette.glow} />
          <circle cx="50" cy="57" r="2.5" fill={palette.accent} />
          <path d="M19 48 8 40l4 18 9 5M81 48l11-8-4 18-9 5" fill={palette.skin} opacity=".65" />
          <path d="M22 78h56M29 86h42" stroke={palette.accent} strokeWidth="1" opacity=".4" />
          <circle cx="50" cy="30" r="4" fill="none" stroke={palette.accent} strokeWidth="1.5" />
          <path d="M50 24v-5M45 27l-3-4M55 27l3-4" stroke={palette.accent} strokeWidth="1.2" />
        </g>
      );
      break;
    case 'hologram':
      portrait = (
        <g>
          <ellipse cx="50" cy="32" rx="29" ry="10" fill="none" stroke={palette.accent} strokeWidth="1" opacity=".55" />
          {shoulders}
          <path d="M29 47c0-14 8-23 21-23s21 9 21 23v13c0 11-8 19-21 19s-21-8-21-19V47Z" fill={palette.skin} opacity=".58" />
          <path d="M31 44c3-10 9-15 19-15s16 5 19 15" fill={palette.accent} opacity=".2" />
          {eyes}
          <path d="M40 70h20" stroke={palette.accent} strokeWidth="1.4" strokeLinecap="round" opacity=".6" />
          <path d="M25 56h50M28 63h44M34 76h32" stroke={palette.glow} strokeWidth=".9" opacity=".35" />
          <rect x="17" y="48" width="5" height="18" fill={palette.accent} opacity=".55" />
          <rect x="78" y="48" width="5" height="18" fill={palette.accent} opacity=".55" />
          <path d="M67 22l3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7Z" fill={palette.glow} opacity=".8" />
        </g>
      );
      break;
    case 'bio':
      portrait = (
        <g>
          {shoulders}
          <path d="M28 48c-11-10-10-25 3-30 4 8 8 12 15 14M72 48c11-10 10-25-3-30-4 8-8 12-15 14" fill={palette.accent} opacity=".7" />
          <path d="M29 45c1-13 9-21 21-21s20 8 21 21v14c0 12-9 20-21 20s-21-8-21-20V45Z" fill={palette.skin} />
          <path d="M34 40c4-7 9-10 16-10s12 3 16 10" stroke={palette.deep} strokeWidth="2" fill="none" opacity=".55" />
          {eyes}
          <path d="M40 70c4 3 16 3 20 0" stroke={palette.deep} strokeWidth="1.5" strokeLinecap="round" />
          <path d="M32 75c-1 8 2 13 8 15M68 75c1 8-2 13-8 15" stroke={palette.accent} strokeWidth="1.4" fill="none" opacity=".65" />
          <circle cx="34" cy="64" r="2.2" fill={palette.glow} opacity=".7" />
          <circle cx="66" cy="64" r="2.2" fill={palette.glow} opacity=".7" />
          <path d="M50 24v-6M45 20l5 4 5-4" stroke={palette.accent} strokeWidth="1.2" fill="none" />
        </g>
      );
      break;
    case 'hydro':
      portrait = (
        <g>
          {shoulders}
          <path d="M29 43c0-13 9-22 21-22s21 9 21 22v18c0 11-9 19-21 19s-21-8-21-19V43Z" fill={palette.skin} opacity=".92" />
          <path d="M28 42c4-12 11-19 22-19s18 7 22 19" fill={palette.deep} opacity=".62" />
          <path d="M31 49c5-4 11-6 19-6s14 2 19 6v13c0 8-8 14-19 14s-19-6-19-14V49Z" fill="#04131e" opacity=".7" />
          {eyes}
          <path d="M41 70c5 3 13 3 18 0" stroke={palette.accent} strokeWidth="1.4" strokeLinecap="round" opacity=".7" />
          <path d="M23 45c-6-4-8-11-5-17 4 5 8 7 12 8M77 45c6-4 8-11 5-17-4 5-8 7-12 8" fill={palette.accent} opacity=".6" />
          <circle cx="82" cy="33" r="2" fill={palette.glow} opacity=".8" />
          <circle cx="87" cy="41" r="1.3" fill={palette.glow} opacity=".6" />
          <path d="M30 30c4-8 10-13 20-15" stroke={palette.glow} strokeWidth="1" fill="none" opacity=".5" />
        </g>
      );
      break;
    case 'beast':
      portrait = (
        <g>
          {shoulders}
          <path d="M29 36 19 16l17 7M71 36l10-20-17 7" fill={palette.skin} />
          <path d="M27 47c0-14 9-22 23-22s23 8 23 22v14c0 12-9 20-23 20s-23-8-23-20V47Z" fill={palette.skin} />
          <path d="M31 43c4-9 10-14 19-14s15 5 19 14" stroke={palette.deep} strokeWidth="2" fill="none" opacity=".6" />
          {eyes}
          <path d="M38 68h24l-4 9H42l-4-9Z" fill={palette.deep} />
          <path d="M43 69v6M50 69v7M57 69v6" stroke={palette.skin} strokeWidth="1.2" />
          <path d="M31 59h-8M69 59h8" stroke={palette.accent} strokeWidth="1.5" opacity=".55" />
          <path d="M22 68c2 11 7 17 16 20M78 68c-2 11-7 17-16 20" stroke={palette.accent} strokeWidth="1.2" fill="none" opacity=".5" />
          <circle cx="33" cy="60" r="2" fill={palette.glow} />
          <circle cx="67" cy="60" r="2" fill={palette.glow} />
        </g>
      );
      break;
    case 'android':
    default:
      portrait = (
        <g>
          {shoulders}
          <path d="M30 44c0-14 9-23 20-23s20 9 20 23v13c0 13-9 22-20 22s-20-9-20-22V44Z" fill={palette.skin} />
          <path d="M31 42c4-11 10-17 19-17s15 6 19 17" fill={palette.deep} opacity=".55" />
          <path d="M34 45h32l-2 10H36l-2-10Z" fill="#050b15" opacity=".82" />
          {eyes}
          <path d="M42 70c4 2 12 2 16 0" stroke={palette.accent} strokeWidth="1.2" strokeLinecap="round" opacity=".6" />
          <path d="M24 49h6v17h-6zM70 49h6v17h-6z" fill={palette.accent} opacity=".55" />
          <path d="M50 21V13" stroke={palette.accent} strokeWidth="2" strokeLinecap="round" />
          <circle cx="50" cy="11" r="3.5" fill={palette.glow} />
          <path d="M31 28c4-6 9-9 19-9s15 3 19 9" stroke={palette.accent} strokeWidth="1.3" fill="none" opacity=".65" />
          <circle cx="26" cy="58" r="1.5" fill={palette.glow} />
          <circle cx="74" cy="58" r="1.5" fill={palette.glow} />
        </g>
      );
      break;
  }

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={`avatar-art avatar-art--${kind} ${className}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : `Avatar ${name}`}
      aria-hidden={decorative || undefined}
    >
      <defs>
        <linearGradient id={bg} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={palette.deep} />
          <stop offset=".58" stopColor="#07101f" />
          <stop offset="1" stopColor={palette.accent} stopOpacity=".42" />
        </linearGradient>
        <radialGradient id={shade} cx=".35" cy=".18" r=".9">
          <stop offset="0" stopColor="#ffffff" stopOpacity=".22" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <pattern id={scan} width="4" height="4" patternUnits="userSpaceOnUse">
          <path d="M0 3.5h4" stroke={palette.accent} strokeWidth=".35" opacity=".3" />
        </pattern>
      </defs>
      <rect width="100" height="100" rx="24" fill={`url(#${bg})`} />
      <rect width="100" height="100" rx="24" fill={`url(#${shade})`} />
      <path d="M8 20h10M82 20h10M8 80h10M82 80h10" stroke={palette.accent} strokeWidth=".8" opacity=".45" />
      <path d="M10 10h16M74 10h16M10 90h16M74 90h16" stroke={palette.glow} strokeWidth=".8" opacity=".3" />
      <rect x="3" y="3" width="94" height="94" rx="22" fill={`url(#${scan})`} opacity=".22" />
      <ellipse cx="50" cy="57" rx="34" ry="27" fill={palette.accent} opacity=".08" />
      {portrait}
      <path d="M11 89h13M76 89h13" stroke={palette.accent} strokeWidth=".7" opacity=".55" />
      <text x="12" y="94" fill={palette.accent} opacity=".8" fontSize="4.2" fontFamily="Consolas, monospace" letterSpacing=".4">
        {serial}
      </text>
      <rect x="1" y="1" width="98" height="98" rx="23" fill="none" stroke={palette.accent} strokeOpacity=".22" />
    </svg>
  );
}
